import { supabase } from "@/lib/supabase";

// memory_favorites is embedded with !left so memories without any favorite
// row still come through. RLS on memory_favorites limits the embedded array
// to rows where user_id = auth.uid(), so length > 0 ⇔ "favorited by me".
const DETAIL_SELECT =
  "*, memory_participants(id,child_id,space_member_id,display_name,children:children(id,name,avatar_url,avatar_color),space_members:space_members(id,display_name,user_id)), collection_memories(collection_id, collection:collections(id, name, icon, color)), memory_favorites!left(user_id)";

/** Fetches full memory detail for MemoryDetailSheet. Returns null when not found or RLS-denied.
 *  Augments the row with derived `is_favorited` based on the embedded memory_favorites array. */
export async function fetchMemoryDetail(memoryId: string) {
  const { data } = await supabase
    .from("memories")
    .select(DETAIL_SELECT)
    .eq("id", memoryId)
    .maybeSingle();
  if (!data) return null;
  const row = data as Record<string, unknown> & { memory_favorites?: Array<{ user_id: string }> };
  return {
    ...row,
    is_favorited: (row.memory_favorites?.length ?? 0) > 0,
  };
}

/** Fetches the card title and memories from the same card for the same children. */
export async function fetchCardContext(
  contentLibraryId: string,
  childIds: string[],
  currentMemoryId: string,
): Promise<{ cardTitle: string | null; otherTimes: Array<{ id: string; title: string | null; created_at: string }> }> {
  const titleRes = await supabase
    .from("content_library")
    .select("title")
    .eq("id", contentLibraryId)
    .single();

  const cardTitle = (titleRes.data as { title: string | null } | null)?.title ?? null;

  if (childIds.length === 0) {
    return { cardTitle, otherTimes: [] };
  }

  const relatedRes = await supabase
    .from("memories")
    .select("id, title, created_at, memory_participants!inner(child_id)")
    .eq("content_library_id", contentLibraryId)
    .neq("id", currentMemoryId)
    .in("memory_participants.child_id", childIds)
    .order("created_at", { ascending: false })
    .limit(5);

  const otherTimes = (relatedRes.data ?? []).map((m) => ({
    id: m.id as string,
    title: (m as { title: string | null }).title,
    created_at: m.created_at as string,
  }));

  return { cardTitle, otherTimes };
}
